import { ButtonContent } from '../ui/ButtonContent';
/**
 * ParticipantInviteView - one progressive composer for one or many people.
 *
 * Recipient tokens replace the previous single, paste, and CSV mode switcher.
 * Typing, pasting, and CSV import all converge on the same visible list. Extra
 * person details stay collapsed until requested and are edited per recipient.
 *
 * @module components/admin/ParticipantInviteView
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import {
  IconArrowLeft,
  IconChevronDown,
  IconFileUpload,
  IconX,
} from '@tabler/icons-react';
import { isValidEmail } from '../../lib/validation';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import type {
  AddParticipantInput,
  AddParticipantsResult,
  AddParticipantsHandler,
  ParticipantImportPreviewReceipt,
  ParticipantImportResult,
} from '../../services/automationService';
import {
  AUTOMATION_OPERATION_POLL_MS,
  AUTOMATION_OPERATION_TIMEOUT_MS,
  clearPendingParticipantImport,
  readPendingParticipantImport,
  writePendingParticipantImport,
  type PendingParticipantImportOperation,
} from '../../lib/api-contracts/automation/operation-lifecycle';

/** Largest atomic operation accepted by the participant endpoint. */
const IMPORT_OPERATION_LIMIT = 500;
/** Largest CSV read by the browser and accepted by the server. */
const CSV_FILE_LIMIT_BYTES = 1024 * 1024;
/** Maximum people displayed on the consequence review. */
const REVIEW_SAMPLE_SIZE = 3;

/** IANA timezone values supported by the current runtime. */
const TIMEZONES: string[] = (() => {
  const intl = Intl as { supportedValuesOf?: (key: string) => string[] };
  try {
    return intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
  } catch {
    return [];
  }
})();

/** Split text on recipient delimiters without splitting inside angle brackets. */
function splitRecipientEntries(text: string): string[] {
  const entries: string[] = [];
  let current = '';
  let insideAddress = false;
  for (const character of text) {
    if (character === '<') insideAddress = true;
    if (character === '>') insideAddress = false;
    if (!insideAddress && (character === ',' || character === ';' || character === '\n')) {
      if (current.trim()) entries.push(current.trim());
      current = '';
    } else {
      current += character;
    }
  }
  if (current.trim()) entries.push(current.trim());
  return entries;
}

/**
 * Parse recipient text in either `email` or `Name <email>` shape.
 *
 * @param text Raw typed or pasted recipient text.
 */
export function parseRecipientText(text: string): AddParticipantInput[] {
  const rows: AddParticipantInput[] = [];
  for (const entry of splitRecipientEntries(text.replace(/\r/g, ''))) {
    const named = entry.match(/^(.+?)\s*<\s*([^>]+)\s*>$/);
    if (named) {
      const email = named[2].trim();
      const name = named[1].trim().replace(/^["']|["']$/g, '');
      if (isValidEmail(email)) rows.push(name ? { email, name } : { email });
      continue;
    }
    for (const value of entry.split(/\s+/)) {
      const email = value.trim();
      if (isValidEmail(email)) rows.push({ email });
    }
  }
  return rows;
}

/** Split one CSV line while respecting double-quoted cells. */
function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (inQuotes) {
      if (character === '"') {
        if (line[index + 1] === '"') {
          current += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += character;
      }
    } else if (character === '"') {
      inQuotes = true;
    } else if (character === ',') {
      cells.push(current);
      current = '';
    } else {
      current += character;
    }
  }
  cells.push(current);
  return cells.map((cell) => cell.trim());
}

const EMAIL_ALIASES = ['email', 'e-mail', 'mail', 'email address', 'e-mail address'];
const NAME_ALIASES = ['name', 'full name', 'full_name', 'first name', 'first_name', 'firstname'];
const LAST_NAME_ALIASES = ['last name', 'last_name', 'lastname', 'surname'];
const COMPANY_ALIASES = ['company', 'organization', 'organisation'];
const PHONE_ALIASES = ['phone', 'phone number', 'mobile', 'tel'];
const TIMEZONE_ALIASES = ['timezone', 'time zone', 'tz'];

/**
 * Parse a CSV into the participant endpoint shape.
 *
 * @param text Raw CSV contents.
 */
export function parseCsv(text: string): AddParticipantInput[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length === 0) return [];
  const header = splitCsvLine(lines[0]).map((cell) => cell.toLowerCase());
  const columnFor = (aliases: string[]): number => {
    for (const alias of aliases) {
      const index = header.indexOf(alias);
      if (index >= 0) return index;
    }
    return -1;
  };

  const emailIndex = columnFor(EMAIL_ALIASES);
  const nameIndex = columnFor(NAME_ALIASES);
  const lastNameIndex = columnFor(LAST_NAME_ALIASES);
  const companyIndex = columnFor(COMPANY_ALIASES);
  const phoneIndex = columnFor(PHONE_ALIASES);
  const timezoneIndex = columnFor(TIMEZONE_ALIASES);
  const hasHeader = [
    emailIndex,
    nameIndex,
    companyIndex,
    phoneIndex,
    timezoneIndex,
  ].some((index) => index >= 0);

  if (hasHeader) {
    const rows: AddParticipantInput[] = [];
    for (let rowIndex = 1; rowIndex < lines.length; rowIndex += 1) {
      const cells = splitCsvLine(lines[rowIndex]);
      const email = (emailIndex >= 0 ? cells[emailIndex] : cells[0]) ?? '';
      if (!email) continue;
      const row: AddParticipantInput = { email };
      const names: string[] = [];
      if (nameIndex >= 0 && cells[nameIndex]) names.push(cells[nameIndex]);
      if (lastNameIndex >= 0 && cells[lastNameIndex]) names.push(cells[lastNameIndex]);
      if (names.length > 0) row.name = names.join(' ');
      if (companyIndex >= 0 && cells[companyIndex]) row.company = cells[companyIndex];
      if (phoneIndex >= 0 && cells[phoneIndex]) row.phone = cells[phoneIndex];
      if (timezoneIndex >= 0 && cells[timezoneIndex]) row.timezone = cells[timezoneIndex];
      rows.push(row);
    }
    return rows;
  }

  const firstCells = splitCsvLine(lines[0]);
  const detectedEmailIndex = Math.max(0, firstCells.findIndex((cell) => isValidEmail(cell)));
  return lines.flatMap((line) => {
    const cells = splitCsvLine(line);
    const email = cells[detectedEmailIndex];
    if (!email) return [];
    const row: AddParticipantInput = { email };
    const remaining = cells.filter((cell, index) => index !== detectedEmailIndex && cell !== '');
    if (remaining[0]) row.name = remaining[0];
    if (remaining[1]) row.company = remaining[1];
    return [row];
  });
}

/** Merge rows by case-insensitive email while keeping the first occurrence. */
function mergeRecipients(
  current: AddParticipantInput[],
  incoming: AddParticipantInput[],
): AddParticipantInput[] {
  const seen = new Set(current.map((row) => row.email.toLowerCase()));
  const merged = [...current];
  for (const row of incoming) {
    const key = row.email.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
}

/** Build the terse outcome shown after one atomic invite operation. */
function outcomeLabel(result: AddParticipantsResult): string {
  const totals = {
    added: result.counts.added,
    duplicate: result.counts.duplicate,
    invalid: result.counts.invalid_email,
    queued: result.dispatch?.immediately_queued ?? 0,
    scheduled: result.dispatch?.recovery_scheduled ?? 0,
  };
  const parts: string[] = [];
  const dispatched = totals.queued + totals.scheduled;
  if (totals.queued) parts.push(`${totals.queued} invitation${totals.queued === 1 ? '' : 's'} queued`);
  if (totals.scheduled) parts.push(`${totals.scheduled} invitation${totals.scheduled === 1 ? '' : 's'} scheduled`);
  if (totals.added > dispatched) parts.push(`${totals.added - dispatched} added`);
  if (totals.duplicate) parts.push(`${totals.duplicate} duplicate${totals.duplicate === 1 ? '' : 's'}`);
  if (totals.invalid) parts.push(`${totals.invalid} invalid`);
  return parts.join(', ') || 'Nothing added';
}

/** Frozen operation shown on the explicit consequence review. */
interface ParticipantImportReview {
  /** Immutable server receipt retained across failed or ambiguous retries. */
  receipt: ParticipantImportPreviewReceipt;
}

/** True when an import result is the durable cancellation terminal. */
function isCancelledImport(result: ParticipantImportResult): boolean {
  return !('phase' in result) && result.receipt.status === 'cancelled';
}

/** Read a phase only from results that carry one. */
function importPhase(
  result: ParticipantImportResult,
): 'preview' | 'confirmed' | 'unconfirmed' | 'cancelled' {
  return 'phase' in result ? result.phase : 'cancelled';
}

/** Props accepted by {@link ParticipantInviteView}. */
export interface ParticipantInviteViewProps {
  /** Campaign that owns the durable preview receipt. */
  campaignId: string;
  /** Return to the main invite choices. */
  onBack: () => void;
  /** Preview and confirm one server-owned participant import. */
  onInvite: AddParticipantsHandler;
  /** Called after the atomic operation succeeds. */
  onInvited?: () => void;
}

/** Render the focused participant invite interaction. */
export const ParticipantInviteView: React.FC<ParticipantInviteViewProps> = ({
  campaignId,
  onBack,
  onInvite,
  onInvited,
}) => {
  const [recipients, setRecipients] = useState<AddParticipantInput[]>([]);
  const [draft, setDraft] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRecipientEmail, setDetailsRecipientEmail] = useState<string | null>(null);
  const [csvName, setCsvName] = useState<string | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [review, setReview] = useState<ParticipantImportReview | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const reviewRef = useRef<ParticipantImportReview | null>(review);
  reviewRef.current = review;

  /** Clear local entry state only after a durable successful confirmation. */
  const applyConfirmedResult = useCallback((result: AddParticipantsResult): void => {
    setOutcome(outcomeLabel(result));
    setRecipients([]);
    setDraft('');
    setDetailsOpen(false);
    setDetailsRecipientEmail(null);
    setCsvName(null);
    setCsvFile(null);
    setReview(null);
    clearPendingParticipantImport(campaignId);
    onInvited?.();
  }, [campaignId, onInvited]);

  /** Poll one confirmation and request durable cancellation after 30 seconds. */
  const reconcileImport = useCallback(async (
    operation: PendingParticipantImportOperation,
    isActive: () => boolean,
    initialResult?: ParticipantImportResult,
  ): Promise<ParticipantImportResult> => {
    let result = initialResult ?? await onInvite({
      action: 'status',
      previewId: operation.previewId,
    });
    while (importPhase(result) === 'unconfirmed' && isActive()) {
      const startedAt = operation.confirmationStartedAt ?? Date.now();
      if (Date.now() - startedAt >= AUTOMATION_OPERATION_TIMEOUT_MS) {
        result = await onInvite({ action: 'cancel', previewId: operation.previewId });
      } else {
        await new Promise((resolve) => window.setTimeout(resolve, AUTOMATION_OPERATION_POLL_MS));
        if (!isActive()) break;
        result = await onInvite({ action: 'status', previewId: operation.previewId });
      }
      if (importPhase(result) === 'unconfirmed') {
        await new Promise((resolve) => window.setTimeout(resolve, AUTOMATION_OPERATION_POLL_MS));
      }
    }
    return result;
  }, [onInvite]);

  // Restore a frozen review or resume its confirmation before accepting input.
  useEffect(() => {
    const pending = readPendingParticipantImport(campaignId);
    if (!pending) return undefined;
    let active = true;
    setSubmitting(true);
    setError(null);
    void (async () => {
      try {
        let result = await onInvite({ action: 'status', previewId: pending.previewId });
        if (!active) return;
        if (pending.confirmationStartedAt === null && importPhase(result) === 'preview') {
          setReview({ receipt: result.receipt as ParticipantImportPreviewReceipt });
          return;
        }
        let operation = pending;
        if (operation.confirmationStartedAt === null) {
          operation = { ...operation, confirmationStartedAt: Date.now() };
          writePendingParticipantImport(campaignId, operation);
        }
        result = await reconcileImport(operation, () => active, result);
        if (!active) return;
        if (importPhase(result) === 'confirmed') {
          applyConfirmedResult(result as AddParticipantsResult);
        } else if (isCancelledImport(result)) {
          clearPendingParticipantImport(campaignId);
          setError('Invite failed');
        }
      } catch (resumeError) {
        captureException(resumeError as Error, 'ParticipantInviteView.resume', {
          previewId: pending.previewId,
        });
      } finally {
        if (active) setSubmitting(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [applyConfirmedResult, campaignId, onInvite, reconcileImport]);

  const pendingDraftRows = useMemo(() => parseRecipientText(draft), [draft]);
  const inviteCount = mergeRecipients(recipients, pendingDraftRows).length;
  const detailsRecipient = recipients.find(
    (recipient) => recipient.email.toLowerCase() === detailsRecipientEmail?.toLowerCase(),
  ) ?? recipients[0];

  const replaceRecipients = (next: AddParticipantInput[]): void => {
    setRecipients(next);
    setReview(null);
    setOutcome(null);
    setError(null);
    setDetailsRecipientEmail((current) => {
      if (current && next.some((row) => row.email.toLowerCase() === current.toLowerCase())) {
        return current;
      }
      return next[0]?.email ?? null;
    });
    if (next.length === 0) {
      setDetailsOpen(false);
    }
  };

  /** Update one optional detail without leaking empty values into the preview. */
  const updateRecipientDetail = (
    email: string,
    field: 'name' | 'company' | 'phone' | 'timezone',
    value: string,
  ): void => {
    setRecipients((current) => current.map((recipient) => {
      if (recipient.email.toLowerCase() !== email.toLowerCase()) return recipient;
      const next = { ...recipient };
      if (value) next[field] = value;
      else delete next[field];
      return next;
    }));
    setReview(null);
    setOutcome(null);
    setError(null);
  };

  const addRecipientRows = (incoming: AddParticipantInput[]): void => {
    if (incoming.length === 0) return;
    const merged = mergeRecipients(recipients, incoming);
    if (merged.length > IMPORT_OPERATION_LIMIT) {
      setError(`Import up to ${IMPORT_OPERATION_LIMIT} people at once`);
      return;
    }
    setCsvName(null);
    setCsvFile(null);
    replaceRecipients(merged);
    setDraft('');
  };

  const commitDraft = (): void => addRecipientRows(parseRecipientText(draft));

  const handleCsv = async (file: File): Promise<void> => {
    if (file.size > CSV_FILE_LIMIT_BYTES) {
      setError('CSV must be 1 MB or less');
      return;
    }
    replaceRecipients([]);
    setDraft('');
    setCsvName(file.name);
    setCsvFile(file);
  };

  /** Ask the server to freeze the source before showing consequence review. */
  const handleReview = async (): Promise<void> => {
    let rows = mergeRecipients(recipients, parseRecipientText(draft));
    if (!csvFile && rows.length === 0) {
      setError('Add an email address');
      return;
    }
    if (rows.length > IMPORT_OPERATION_LIMIT) {
      setError(`Import up to ${IMPORT_OPERATION_LIMIT} people at once`);
      return;
    }
    if (!csvFile) {
      rows = rows.map((row) => ({
        email: row.email,
        ...(row.name?.trim() ? { name: row.name.trim() } : {}),
        ...(row.company?.trim() ? { company: row.company.trim() } : {}),
        ...(row.phone?.trim() ? { phone: row.phone.trim() } : {}),
        ...(row.timezone ? { timezone: row.timezone } : {}),
      }));
      replaceRecipients(rows);
      setDraft('');
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await onInvite(csvFile
        ? { action: 'preview', source: { kind: 'csv', file: csvFile } }
        : { action: 'preview', source: { kind: 'manual', participants: rows } });
      if (importPhase(result) !== 'preview') throw new Error('Expected participant preview');
      const receipt = result.receipt as ParticipantImportPreviewReceipt;
      writePendingParticipantImport(campaignId, {
        previewId: receipt.id,
        confirmationStartedAt: null,
      });
      setReview({ receipt });
    } catch (previewError) {
      captureException(previewError as Error, 'ParticipantInviteView.preview', {
        count: csvFile ? null : rows.length,
      });
      const errorCode = (previewError as { errorCode?: unknown }).errorCode;
      const message = previewError instanceof Error ? previewError.message : '';
      setError(
        errorCode === 'participant_operation_limit' || message.includes('participant_operation_limit')
          ? `Import up to ${IMPORT_OPERATION_LIMIT} people at once`
          : 'Could not preview import',
      );
    } finally {
      setSubmitting(false);
    }
  };

  /** Submit the frozen review, retaining its key on failure for safe retry. */
  const handleInvite = async (): Promise<void> => {
    if (!review) return;
    setSubmitting(true);
    setError(null);
    try {
      const operation: PendingParticipantImportOperation = {
        previewId: review.receipt.id,
        confirmationStartedAt: Date.now(),
      };
      writePendingParticipantImport(campaignId, operation);
      let initialResult: ParticipantImportResult;
      try {
        initialResult = await onInvite({ action: 'confirm', previewId: operation.previewId });
      } catch (confirmError) {
        captureException(confirmError as Error, 'ParticipantInviteView.confirmAmbiguous', {
          previewId: operation.previewId,
        });
        initialResult = await onInvite({ action: 'status', previewId: operation.previewId });
      }
      const result = await reconcileImport(
        operation,
        () => reviewRef.current?.receipt.id === operation.previewId,
        initialResult,
      );
      if (reviewRef.current?.receipt.id !== operation.previewId) return;
      if (importPhase(result) === 'confirmed') {
        applyConfirmedResult(result as AddParticipantsResult);
      } else if (isCancelledImport(result)) {
        clearPendingParticipantImport(campaignId);
        setError('Invite failed');
      }
    } catch (inviteError) {
      captureException(inviteError as Error, 'ParticipantInviteView.invite', {
        count: review.receipt.requested_count,
        previewId: review.receipt.id,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={onBack}
        className="flex min-h-11 touch-manipulation cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
      >
        <IconArrowLeft aria-hidden="true" className="h-4 w-4" />
        Back
      </button>

      {review ? (
        <section className="space-y-4" aria-labelledby="participant-import-review-title">
          <div className="space-y-1">
            <h3
              id="participant-import-review-title"
              className="text-base font-semibold text-foreground"
            >
              Review {review.receipt.requested_count} {review.receipt.requested_count === 1 ? 'person' : 'people'}
            </h3>
            <p className="text-sm text-muted-foreground">Invitations start after confirmation.</p>
          </div>
          <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface-raised">
            {review.receipt.sample.slice(0, REVIEW_SAMPLE_SIZE).map((row) => (
              <li key={row.email.toLowerCase()} className="px-3 py-2.5">
                {row.name ? (
                  <>
                    <span className="block text-sm font-medium text-foreground">{row.name}</span>
                    <span className="block text-sm text-muted-foreground">{row.email}</span>
                  </>
                ) : (
                  <span className="block text-sm text-foreground">{row.email}</span>
                )}
              </li>
            ))}
          </ul>
          {review.receipt.requested_count > REVIEW_SAMPLE_SIZE ? (
            <p className="text-sm text-muted-foreground">
              +{review.receipt.requested_count - REVIEW_SAMPLE_SIZE} more
            </p>
          ) : null}
        </section>
      ) : (
      <>
      <div className="space-y-2">
        <label htmlFor="participant-recipient-input" className="block text-sm font-medium text-foreground">
          Email addresses
        </label>
        <div
          className="flex min-h-12 flex-wrap items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 transition-colors focus-within:border-volt focus-within:ring-2 focus-within:ring-volt/20 motion-reduce:transition-none"
          onClick={() => inputRef.current?.focus()}
        >
          {recipients.map((recipient) => (
            <span
              key={recipient.email.toLowerCase()}
              className="flex max-w-full items-center gap-1.5 rounded-lg bg-wash-accent px-2 py-1 text-sm text-foreground"
            >
              <span className="truncate">{recipient.name || recipient.email}</span>
              <button
                type="button"
                aria-label={`Remove ${recipient.email}`}
                onClick={(event) => {
                  event.stopPropagation();
                  replaceRecipients(recipients.filter((row) => row.email !== recipient.email));
                }}
                className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
              >
                <IconX className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            id="participant-recipient-input"
            type="text"
            name="participantRecipients"
            autoComplete="off"
            spellCheck={false}
            value={draft}
            onChange={(event) => {
              const nextDraft = event.target.value;
              if (csvFile) {
                setCsvFile(null);
                setCsvName(null);
              }
              if (/[,;\n]/u.test(nextDraft)) {
                addRecipientRows(parseRecipientText(nextDraft));
                return;
              }
              setDraft(nextDraft);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
                event.preventDefault();
                commitDraft();
              } else if (event.key === 'Backspace' && !draft && recipients.length > 0) {
                replaceRecipients(recipients.slice(0, -1));
              }
            }}
            onBlur={() => {
              if (pendingDraftRows.length > 0) commitDraft();
            }}
            placeholder={recipients.length > 0 ? 'Add another' : 'Email, then Enter'}
            className="min-h-11 min-w-40 flex-1 bg-transparent py-1 text-sm text-foreground placeholder:text-subtle-foreground focus:outline-none"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex min-h-11 touch-manipulation cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground focus-within:ring-2 focus-within:ring-volt motion-reduce:transition-none">
          <IconFileUpload className="h-4 w-4" />
          Import CSV
          <input
            data-testid="invite-csv-file"
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void handleCsv(file);
            }}
          />
        </label>
        {csvName && (
          <span className="text-sm text-muted-foreground">
            {csvName}
          </span>
        )}
      </div>

      {recipients.length > 0 && pendingDraftRows.length === 0 && (
        <div>
          <button
            type="button"
            onClick={() => setDetailsOpen((open) => !open)}
            aria-expanded={detailsOpen}
            className="flex min-h-11 touch-manipulation cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
          >
            <IconChevronDown className={`h-4 w-4 transition-transform duration-200 motion-reduce:transition-none ${detailsOpen ? 'rotate-180' : ''}`} />
            <AnimatedLabel text={detailsOpen ? 'Hide details' : 'Add details'} reserveText="Hide details" />
          </button>
          <AnimatedPresence show={detailsOpen && !!detailsRecipient}>
          {detailsRecipient && (
            <div className="mt-3 space-y-3">
              {recipients.length > 1 && (
                <select
                  name="participantDetailsPerson"
                  autoComplete="off"
                  aria-label="Person details"
                  value={detailsRecipient.email}
                  onChange={(event) => setDetailsRecipientEmail(event.target.value)}
                  className="min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:border-volt focus-visible:ring-2 focus-visible:ring-volt"
                >
                  {recipients.map((recipient) => (
                    <option key={recipient.email.toLowerCase()} value={recipient.email}>
                      {recipient.name ? `${recipient.name} (${recipient.email})` : recipient.email}
                    </option>
                  ))}
                </select>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input
                  type="text"
                  name="participantName"
                  autoComplete="off"
                  aria-label="Name"
                  value={detailsRecipient.name ?? ''}
                  onChange={(event) => updateRecipientDetail(
                    detailsRecipient.email,
                    'name',
                    event.target.value,
                  )}
                  placeholder="Name"
                  className="min-h-11 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-subtle-foreground focus:outline-none focus-visible:border-volt focus-visible:ring-2 focus-visible:ring-volt"
                />
                <input
                  type="text"
                  name="participantCompany"
                  autoComplete="off"
                  aria-label="Company"
                  value={detailsRecipient.company ?? ''}
                  onChange={(event) => updateRecipientDetail(
                    detailsRecipient.email,
                    'company',
                    event.target.value,
                  )}
                  placeholder="Company"
                  className="min-h-11 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-subtle-foreground focus:outline-none focus-visible:border-volt focus-visible:ring-2 focus-visible:ring-volt"
                />
                <input
                  type="tel"
                  name="participantPhone"
                  autoComplete="off"
                  aria-label="Phone"
                  value={detailsRecipient.phone ?? ''}
                  onChange={(event) => updateRecipientDetail(
                    detailsRecipient.email,
                    'phone',
                    event.target.value,
                  )}
                  placeholder="Phone"
                  className="min-h-11 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-subtle-foreground focus:outline-none focus-visible:border-volt focus-visible:ring-2 focus-visible:ring-volt"
                />
                <select
                  name="participantTimezone"
                  autoComplete="off"
                  aria-label="Timezone"
                  value={detailsRecipient.timezone ?? ''}
                  onChange={(event) => updateRecipientDetail(
                    detailsRecipient.email,
                    'timezone',
                    event.target.value,
                  )}
                  className="min-h-11 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:border-volt focus-visible:ring-2 focus-visible:ring-volt"
                >
                  <option value="">Timezone</option>
                  {TIMEZONES.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </div>
            </div>
          )}
          </AnimatedPresence>
        </div>
      )}

      <button aria-busy={submitting}
        type="button"
        disabled={submitting || (!csvFile && inviteCount === 0)}
        onClick={() => void handleReview()}
        className="min-h-11 w-full touch-manipulation cursor-pointer rounded-lg button-accent px-4 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
      >
        <ButtonContent loading={submitting}>{inviteCount > 0 ? `Review ${inviteCount}` : 'Review'}</ButtonContent>
      </button>
      </>
      )}

      {review ? (
        <button aria-busy={submitting}
          type="button"
          disabled={submitting}
          onClick={() => void handleInvite()}
          className="min-h-11 w-full touch-manipulation cursor-pointer rounded-lg button-accent px-4 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
        >
          <ButtonContent loading={submitting}>{`Invite ${review.receipt.requested_count}`}</ButtonContent>
        </button>
      ) : null}

      {outcome && (
        <p role="status" aria-live="polite" className="text-sm text-foreground">
          {outcome}
        </p>
      )}
      {error && (
        <p role="alert" className="rounded-lg bg-wash-danger px-3 py-2 text-sm text-danger-soft">
          {error}
        </p>
      )}
    </div>
  );
};
